import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import * as CONSTANT from '../../utils/constants';
import { api } from '../../utils/endpoints';
import { BrowserStorageService } from './common/browser-storage.service';
import { ApiCallsService } from './network/api-calls.service';

@Injectable({
  providedIn: 'root',
})
export class CommonDataSharingService {
  oldElementPage = 1;
  oldPage = 1;
  rolesList: any = [];
  private regionsList: any = [];
  private categories: any = [];
  editRequestReasons: any = [];
  public headerProfile: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public locationChange: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public notificationIsRead: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public notificationMarkAllRead: BehaviorSubject<any> = new BehaviorSubject<any>(false);
  public notificationSidebarToHeader: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  sportList: any = [];
  elementList: any = [];
  searchValue: any;

  private searchQuerySubject = new BehaviorSubject<any>('');
  searchQuery$ = this.searchQuerySubject.asObservable();

  private appsyncEndpoint = (environment as any).appsyncEndpoint;
  private apiKey = (environment as any).apiKey;
  constructor(
    private _apiService: ApiCallsService,
    private _storageService: BrowserStorageService,
  ) {
    // this.searchQuerySubject = new BehaviorSubject<string | null>(this._storageService.getItem('searchQuery') || null);
  }

  getRolesList() {
    if (this.rolesList.length == 0) {
      this._apiService.get(api._roles).subscribe({
        next: (res: any) => {
          this.rolesList = res.data.data;
        },
        error: (error: any) => console.log(error),
      });
    }
  }

  getRegionsList(): Observable<any> {
    if (this.regionsList.length == 0) {
      return this._apiService.get(api._regions).pipe(
        map((res: any) => {
          this.regionsList = res.data.data.map((x: any) => {
            return { id: x.name, name: x.name, sId: x.name };
          });
          return this.regionsList;
        }),
      );
    } else {
      return of(this.regionsList);
    }
  }

  private get _modifyOrderStatuses(): any {
    const orderStatues = CONSTANT._clone(CONSTANT.orderStatuses);
    return Object.entries(orderStatues).map(([id, name]) => ({ id: name, name: name, sId: id }));
  }

  private get _modifyDatesFilter(): any {
    return CONSTANT.datesFilter;
  }

  private get _modifyUsersStatuses(): any {
    const usersStatuses = CONSTANT._clone(CONSTANT.userStatuses);
    return Object.entries(usersStatuses).map(([id, name]) => ({ id: name, name: name, sId: id }));
  }

  private get _modifyQuotationStatuses(): any {
    const quotationStatus = CONSTANT._clone(CONSTANT.quotationStatuses);
    return Object.entries(quotationStatus).map(([id, name]) => ({ id: name, name: name, sId: id }));
  }

  private get _modifyTicketReplacementStatuses(): any {
    const ticketReplacementStatuses = CONSTANT._clone(CONSTANT.ticketReplacementStatuses);
    return Object.entries(ticketReplacementStatuses).map(([id, name]) => ({ id: name, name: name, sId: id }));
  }

  private get _modifyGeneralTicketStatuses(): any {
    const ticketGeneralStatuses = CONSTANT._clone(CONSTANT.ticketGeneralStatuses);
    return Object.entries(ticketGeneralStatuses).map(([id, name]) => ({ id: name, name: name, sId: id }));
  }

  private get _modifyPickStatuses(): any {
    const pickupStatuses = CONSTANT._clone(CONSTANT.pickupStatuses);
    return Object.entries(pickupStatuses).map(([id, name]) => ({ id: name, name: name, sId: id }));
  }

  private get _modifyInvoiceStatuses(): any {
    const invoiceStatus = CONSTANT._clone(CONSTANT.invoiceStatus);
    return Object.entries(invoiceStatus).map(([id, name]) => ({ id: name, name: name, sId: id }));
  }

  getOrdersFilters(): Observable<any> {
    return forkJoin({
      status: of(this._modifyOrderStatuses),
      dates: of(this._modifyDatesFilter),
    }).pipe(
      map(({ status, dates }) => [
        { name: 'Status', children: status },
        { name: 'Expected Delivery Date', type: 'dateRange', children: dates },
      ]),
      catchError((err: any) => []),
    );
  }

  getUsersFilters(): any {
    return of([{ name: 'Status', children: this._modifyUsersStatuses }]);
  }

  getQuotationFilters(): Observable<any> {
    return forkJoin({
      status: of(this._modifyQuotationStatuses),
      dates: of(this._modifyDatesFilter),
    }).pipe(
      map(({ status, dates }) => [
        { name: 'Status', children: status },
        { name: 'Quotation Date', type: 'dateRange', children: dates },
      ]),
      catchError((err: any) => []),
    );
  }

  getCategoriesSubCategories(): Observable<any> {
    if (this.categories.length == 0) {
      return this._apiService.get(api._categories).pipe(
        map((res: any) => {
          this.categories = res.data.rows.map(({ subcategories, ...rest }: any) => ({ ...rest, children: subcategories }));
          return this.categories;
        }),
      );
    } else {
      return of(this.categories);
    }
  }

  getReplacementTicketFilters(): Observable<any> {
    return forkJoin({
      status: of(this._modifyTicketReplacementStatuses),
      dates: of(this._modifyDatesFilter),
    }).pipe(
      map(({ status, dates }) => [
        { name: 'Status', children: status },
        { name: 'Ticket Date', type: 'dateRange', children: dates },
      ]),
      catchError((err: any) => []),
    );
  }

  getGeneralTicketFilters(): Observable<any> {
    return forkJoin({
      status: of(this._modifyGeneralTicketStatuses),
      dates: of(this._modifyDatesFilter),
    }).pipe(
      map(({ status, dates }) => [
        { name: 'Status', children: status },
        { name: 'Ticket Date', type: 'dateRange', children: dates },
      ]),
      catchError((err: any) => []),
    );
  }

  getPickupFilters(): Observable<any> {
    return forkJoin({
      status: of(this._modifyPickStatuses),
      dates: of(this._modifyDatesFilter),
    }).pipe(
      map(({ status, dates }) => [
        { name: 'Status', children: status },
        { name: 'Date', type: 'dateRange', children: dates },
      ]),
      catchError((err: any) => []),
    );
  }

  getInvoiceFilters(): Observable<any> {
    return forkJoin({
      status: of(this._modifyInvoiceStatuses),
      dates: of(this._modifyDatesFilter),
    }).pipe(
      map(({ status, dates }) => [
        { name: 'Status', children: status },
        { name: 'Date', type: 'dateRange', children: dates },
      ]),
      catchError((err: any) => []),
    );
  }

  getEditRequestReasonsList(): Observable<any> {
    if (this.editRequestReasons.length == 0) {
      const data = { type: 9, platform: 3 };
      return this._apiService.get(api._getReasonList, data).pipe(
        map((res: any) => {
          this.editRequestReasons = res.data;
          return this.editRequestReasons;
        }),
      );
    } else {
      return of(this.editRequestReasons);
    }
  }

  _getQuotationCount() {
    return this._apiService.get(api._quotationCount);
  }

  _markAsRead(id: any) {
    const request = { id: id, isRead: 1 };
    return this._apiService.putWithoutLoader(api._markRead, request);
  }
  getMYFeedFilters(): any {
    return forkJoin({
      sport: of(this._modifyUserDetailsSport),
      element: of(this._modifyElement),
      status: of(this._modifyUsersDetailsStatuses),
      dates: of(this._modifyDatesFilter),
    }).pipe(
      map(({ status, sport, dates, element }: { status: any; sport: any; dates: any; element: any }) => [
        { name: 'Sports', type: 'checkbox', children: sport },
        { name: 'Elements', type: 'checkbox', children: element },
        { name: 'Status', children: status, type: 'checkbox' },
        { name: 'Registered date', type: 'dateRange', children: dates },
      ]),
      catchError((err: any) => []),
    );
  }
  getFeedFilters(): any {
    return forkJoin({
      sport: of(this._modifyUserDetailsSport),
      status: of(this._modifyUsersDetailsStatuses),
      dates: of(this._modifyDatesFilter),
    }).pipe(
      map(({ status, sport, dates }: { status: any; sport: any; dates: any }) => [
        { name: 'Registered date', type: 'dateRange', children: dates },
        { name: 'Status', children: status, type: 'checkbox' },
        { name: 'Sports', type: 'checkbox', children: sport },
      ]),
      catchError((err: any) => []),
    );
  }
  private get _modifyUserDetailsSport(): any {
    let array: any = this.sportList;
    return (array || []).map((data: any) => ({ id: data.id, name: data.name, sId: data.id }));
  }
  private get _modifyElement(): any {
    let array: any = this.elementList;
    return (array || []).map((data: any) => ({ id: data.id, name: data.name, sId: data.id }));
  }
  _getSportList(searchContent: any, page: any): Observable<any[]> {
    const params: any = {
      scope: ['filterFields'],
      page: page,
      limit: 2000,
    };

    if (searchContent) {
      params['search'] = searchContent;
    }

    params['sortOrder'] = 'ASC';
    params['sortBy'] = 'name';

    // If the sportList is empty or search content is provided, fetch from API
    if (this.sportList.length === 0 || searchContent || (page > 1 && page != this.oldPage) || this.searchValue != searchContent) {
      this.searchValue = searchContent;
      this.oldPage = page;
      // console.log('Fetching sports list from API...');
      return this._apiService.post(api._sportListing, params).pipe(
        tap((res: any) => {
          if (res?.data?.rows) {
            if (searchContent) {
              this.sportList = res.data.rows; // Update cached data
            } else {
              res.data.rows.map((item: any) => {
                this.sportList.push(item);
              });
            }
          } else {
            console.log('No data received from API.');
          }
        }),
        map((res: any) => res?.data?.rows || []), // Transform response for the caller
      );
    } else {
      // Return cached sportList as an Observable
      // console.log('Returning cached sport list:', this.sportList);
      return new Observable((observer: { next: (arg0: any) => void; complete: () => void }) => {
        observer.next(this.sportList);
        observer.complete();
      });
    }
  }
  private get _modifyUsersDetailsStatuses(): any {
    const usersStatuses = CONSTANT._clone(CONSTANT.userDetailsSport);
    return Object.entries(usersStatuses).map(([id, name]) => ({ id: name, name: name, sId: id }));
  }
  _getElementList(searchContent: any, page: any): Observable<any[]> {
    const params: any = {
      // scope: ['filterFields'],
      page: page,

      limit: 20,
    };

    if (searchContent) {
      params['search'] = searchContent;
    }

    // If the sportList is empty or search content is provided, fetch from API
    if (this.elementList.length === 0 || searchContent || (page > 1 && page != this.oldElementPage)) {
      this.oldElementPage = page;
      return this._apiService.get(api._elementListing, params).pipe(
        tap((res: any) => {
          if (res?.data?.rows) {
            this.elementList = res.data.rows; // Update cached data
          } else {
            console.log('No data received from API.');
          }
        }),
        map((res: any) => res?.data?.rows || []), // Transform response for the caller
      );
    } else {
      // Return cached elementList as an Observable
      return new Observable((observer: { next: (arg0: any) => void; complete: () => void }) => {
        observer.next(this.elementList);
        observer.complete();
      });
    }
  }

  getEventFilters(activeSection: any): Observable<any> {
    return forkJoin({
      sport: of(this._modifyUserDetailsSport),
      status: of(this._modifyEventStatuses),
      otherStatus: of(this._modifyEventOtherStatuses),
      dates: of(this._modifyDatesFilter),
    }).pipe(
      map(({ sport, status, dates, otherStatus }) => [
        { name: 'Sports', children: sport, type: 'checkbox' },
        { name: 'Status', children: activeSection === 'myEvents' ? status : otherStatus, type: 'checkbox' },
        { name: 'Event Date', type: 'dateRange', children: dates },
      ]),
      catchError((err: any) => []),
    );
  }
  private get _modifyEventStatuses(): any {
    const usersStatuses = CONSTANT._clone(CONSTANT.EventStatuses);
    return Object.entries(usersStatuses).map(([id, name]) => ({ id: name, name: name, sId: id }));
  }
  private get _modifyEventOtherStatuses(): any {
    const usersStatuses = CONSTANT._clone(CONSTANT.EventOtherStatuses);
    return Object.entries(usersStatuses).map(([id, name]) => ({ id: name, name: name, sId: id }));
  }

  getnewsFilters(): any {
    return forkJoin({
      sport: of(this._modifyUserDetailsSport),
      dates: of(this._modifyDatesFilter),
    }).pipe(
      map(({ sport, dates }: { sport: any; dates: any }) => [
        { name: 'Sports', type: 'checkbox', children: sport },
        { name: 'Date', type: 'dateRange', children: dates },
      ]),
      catchError((err: any) => []),
    );
  }

  formatLevel(input: any, white: any) {
    return (
      input
        .split(' ') // Split by space
        .map((word: any) => (word.toLowerCase() === 'xx' ? 'XX' : word[0].toLowerCase() + word.slice(1))) // Format each word
        .join('-') + // Join with '-'
      (white ? '_white' : '') // Append '-white' if white is true
    );
  }
  _getToggleNotification(limit?: any, skip?: any) {
    return this._apiService.getDataWithoutLoader(api._notificationUser, { page: skip + 1, limit: limit });
  }
  _markAllAsRead(notificationIds: any) {
    return this._apiService.patchWithoutLoader(api._markRead, { notificationIds });
  }
  // like feed
  likeFeed(postId: any, userId: any) {
    const mutation = `mutation MyMutation {
      likePost(postId: \"${postId}\", userId: \"${userId}\") {
         ${CONSTANT.likeContain}
      }
    }`;
    this.graphqlApiCall(mutation);
  }
  // unlike Feed
  unlikeFeed(postId: any, userId: any) {
    const mutation = `mutation MyMutation {
      removeLike(postId: \"${postId}\", userId: \"${userId}\") {
         ${CONSTANT.likeContain}
      }
    }`;
    this.graphqlApiCall(mutation);
  }
  // custom ApI call of graphQl
  graphqlApiCall(mutation: any) {
    this._apiService.loader(true);
    return fetch(this.appsyncEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': this.apiKey,
      },
      body: JSON.stringify({ query: mutation }),
    })
      .then((response) => {
        this._apiService.loader(false);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        this._apiService.loader(false);
        if (data.errors) {
          throw new Error(JSON.stringify(data.errors));
        }

        return data;
      })
      .catch((error) => {
        this._apiService.loader(false);
        console.error('Error adding comment:', error);
        throw error;
      });
  }

  setSearchQuery(query: string) {
    this.searchQuerySubject.next(query);
    this._storageService.setItem('searchQuery', query); // Save to localStorage for persistence
  }

  getSearchQuery() {
    return this.searchQuerySubject.getValue() || this._storageService.getItem('searchQuery');
  }
  getUserLevelTypesFilters(): Observable<any> {
    return forkJoin({
      // dates: '',
      element: of(this._modifyUserLevelTypes),
    }).pipe(
      map(({ element }) => [
        { name: 'Point range', type: 'PriceRange', children: [0] },
        { name: 'Level', children: element, type: 'checkbox' },
      ]),
      catchError((err: any) => []),
    );
  }
  private get _modifyUserLevelTypes(): any {
    const sportTypes = CONSTANT._clone(CONSTANT.userLevelTypes);
    return Object.entries(sportTypes).map(([id, name]) => ({
      id: name,
      name: name,
      sId: id,
    }));
  }
  convertTo12HourParts(timeStr: string, num: any): any {
    const [hourStr, minuteStr] = timeStr.split(':');
    let hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10) == 0 ? Number('00') : parseInt(minuteStr, 10);

    let meridian: 'AM' | 'PM' = hour >= 12 ? 'PM' : 'AM';

    // Convert to 12-hour format
    if (hour === 0) {
      hour = 12;
    } else if (hour > 12) {
      hour = hour - 12;
    }
    if (num == 1) {
      return {
        hour: hour,
        minute: minute,
      };
    } else {
      return meridian;
    }
  }
}
